/* eslint-disable react-hooks/exhaustive-deps */
// cspell: ignore geocode
import { PaginatedResponse } from '@eagle/api-types';
import { PersonThingEvent, ThingEventSnapshot } from '@eagle/core-data-types';
import { FeatureTypes, TimeZone, TrackingEventTypes } from '@eagle/data-function-types';
import { useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useAuthenticated } from '../../auth';
import { usePromise } from '../../hooks';
import { useRecursive } from '../../hooks/use-recursive';
import { EventFields } from '../../pages/event-history/history-drawer/history-drawer.types';
import { useSearch } from '../../pages/list/use-search';
import { CacheDataTypes, Undefinable } from '../../types';
import { getEventFeature, getEventFeatureV2, getEventLabel, getEventLabelV2, getFeatureInstance, validateLocationType } from '../../util';
import { AppliedFilter, EntityField } from '../entity-search';
import { EventCategories } from '../events-table';
import { useBoolFlag, useNumberFlag } from '../flags';
import { TimelineData } from '../timeline';
import { ExportDataProps } from './entity-journey-item';
import { JourneyItem } from './entity-journey-types';
import { useNotableEventsContext } from './notable-events-context';
import { getEventDictionary, isValidEvent } from './util';

interface Props {
  data: JourneyItem;
  onLoaded: (data: ExportDataProps) => void;
  showLocationUpdateEvents: boolean;
}

type EventFiltersType = typeof EventCategories[keyof typeof EventCategories] | unknown[];

const HISTORY_REQUEST_RETRY_COUNT = 2;

export const JourneyInstanceExport: FC<Props> = ({
  data,
  onLoaded,
  showLocationUpdateEvents,
}) => {
  const { restClient } = useAuthenticated();
  const { endTime, startTime } = data.dateRange;
  const { filters } = useSearch();
  const { notableEvents } = useNotableEventsContext();
  const theme = useTheme();
  const isBatchingEnabled = useBoolFlag('track-thing-history-parallel-processing-api-feature');
  const batchSize = useNumberFlag('track-thing-history-parallel-processing-api-batch-settings-feature');
  const isRetryingEnabled = useBoolFlag('track-thing-history-retry-api-calls-feature');
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const flagShowLocationSwitchFeature = useBoolFlag('track-thing-history-switch-show-location-updates-in-journey-instance-card-feature');
  const flagShowLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-location-update-events');
  const flagShowLocationSwitch = flagShowLocationUpdateEvents && flagShowLocationSwitchFeature;
  const flagShowNoLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-no-location-update-events');
  const flagShowInaccurateLocationUpdateEvents = useBoolFlag('track-thing-history-journey-card-show-inaccurate-location-update-events');
  const hasNotableEvent = useRef(false);
  const hasLocationUpdate = useRef(false);

  const eventFilters = filters.flatMap((filter): EventFiltersType => {
    switch ((filter as AppliedFilter<EntityField>).value.id) {
      case EventFields.DRIVER_BEHAVIOR:
        return EventCategories[EventFields.DRIVER_BEHAVIOR];
      case EventFields.MEDIA:
        return EventCategories.media;
      case EventFields.SAFETY_INCIDENT:
        return EventCategories[EventFields.SAFETY_INCIDENT];
      case EventFields.OTHER:
        return EventCategories.other;
    }
    return [];
  });

  const url = data.entityType === CacheDataTypes.THING_TYPE
    ? `/api/v2/thing-event/thing/${data.entityId}`
    : `/api/v2/person-thing-event/person/${data.entityId}`;

  const locationFilterTypes: TrackingEventTypes[] = [];

  if (!flagShowLocationSwitch) {
    locationFilterTypes.push(
      TrackingEventTypes.LOCATION_UPDATE,
      TrackingEventTypes.INACCURATE_LOCATION_UPDATE,
      TrackingEventTypes.NO_LOCATION_UPDATE,
    );
  } else {
    if (!flagShowLocationUpdateEvents) {
      locationFilterTypes.push(TrackingEventTypes.LOCATION_UPDATE);
    }
    if (!flagShowNoLocationUpdateEvents) {
      locationFilterTypes.push(TrackingEventTypes.NO_LOCATION_UPDATE);
    }
    if (!flagShowInaccurateLocationUpdateEvents) {
      locationFilterTypes.push(TrackingEventTypes.INACCURATE_LOCATION_UPDATE);
    }
  }

  const locationFilters = { '$nin': locationFilterTypes };

  const params = {
    dateRangeStart: startTime,
    dateRangeFinish: endTime,
    filter: eventFilters.length
      ? { 'eventTypeId': { '$in': eventFilters, ...locationFilters } }
      : { 'eventTypeId': locationFilters },
    sort: { occurred: 1 },
  };

  const transformData = (response: AxiosResponse<PaginatedResponse<ThingEventSnapshot>, any>): AxiosResponse<PaginatedResponse<ThingEventSnapshot>> => {
    if (data.entityType === CacheDataTypes.THING_TYPE) return response;
    const personData = response.data.items as unknown[] as PersonThingEvent[];
    return { ...response, data: { ...response.data, items: personData.map(({ thingEvent }) => thingEvent) } };
  };

  const [thingEventSnapShots, , thingEventSnapShotsStatus] = useRecursive<ThingEventSnapshot>(
    url,
    params,
    transformData,
    [data, filters],
    isBatchingEnabled ? batchSize : undefined,
    isRetryingEnabled ? HISTORY_REQUEST_RETRY_COUNT : undefined,
  );

  const [timeZoneFallBack] = usePromise<Undefinable<TimeZone>>(async () => {
    const eventWithLocation = thingEventSnapShots?.items.find(({ data: { location } }) => location);
    const validLocation = validateLocationType(() => eventWithLocation?.data.location);
    if (!thingEventSnapShots || thingEventSnapShotsStatus !== 'resolved' || !eventWithLocation || !validLocation) return;
    const timeZone = await restClient.timeZone.get({
      lat: validLocation.latitude,
      long: validLocation.longitude,
      timestamp: eventWithLocation.occurred,
    });
    return { id: timeZone.id };
  }, [restClient, thingEventSnapShots, thingEventSnapShotsStatus]);

  const timeLineData = useMemo(() => {
    hasNotableEvent.current = false;
    hasLocationUpdate.current = false;
    return thingEventSnapShotsStatus === 'resolved'
      ? thingEventSnapShots?.items.map(({ feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest }) => {
        const eventData = getEventDictionary({ feature, featureTypeId, eventId, eventTypeId, occurred, data: { location }, ...rest }, theme, notableEvents);
        if (!eventData) return;
        if (eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE && eventData.eventTypeId !== TrackingEventTypes.INACCURATE_LOCATION_UPDATE && eventData.eventTypeId !== TrackingEventTypes.NO_LOCATION_UPDATE) hasNotableEvent.current = true;
        else hasLocationUpdate.current = true;
        if (!isValidEvent(eventData, showLocationUpdateEvents)) return;
        const eventLocation = validateLocationType(() => location);
        const featureLabel = flagUseEventV3 ? getEventFeature(feature) : getEventFeatureV2(feature);
        const eventTypeLabel = flagUseEventV3 ? getEventLabel(feature, eventTypeId) : getEventLabelV2(featureTypeId, eventTypeId);
        const secondaryChip = getFeatureInstance(feature);
        const primary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? featureLabel : eventTypeLabel;
        const secondary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? eventTypeLabel : featureLabel;
        return {
          color: eventData.styling.backgroundColor,
          date: occurred,
          eventId,
          primary,
          secondary,
          secondaryChip,
          timezone: eventLocation?.timeZone ?? timeZoneFallBack,
        };
      }).filter(Boolean) as Undefinable<TimelineData[]>
      : [];
  }, [thingEventSnapShotsStatus, thingEventSnapShots, theme, showLocationUpdateEvents, flagUseEventV3, timeZoneFallBack]);

  const events = useMemo(() => (
    thingEventSnapShots?.items.filter((eventSnapshot) => {
      const eventData = getEventDictionary(eventSnapshot, theme, notableEvents);
      return eventData && isValidEvent(eventData, showLocationUpdateEvents);
    })
  ), [showLocationUpdateEvents, theme, thingEventSnapShots?.items]);

  useEffect(() => {
    onLoaded({
      endTime,
      events,
      startTime,
      status: thingEventSnapShotsStatus,
      timeLineData,
    });
  }, [timeLineData, events, thingEventSnapShotsStatus, endTime, startTime]);

  return <></>;
};
